import React, { useEffect, useState } from 'react';
import {
	Alert,
	AutoComplete,
	Form,
	Input,
	InputNumber,
	Modal,
	Radio,
	Select,
	Spin,
	Typography,
	message,
} from 'antd';
import { RichTextFormField } from '../../components/RichTextEditor';
import {
	PRODUCTION_QUESTION_BLOCK_HINT,
	getProductionQuestionBlockMessage,
} from '../../utils/productionQuestionBlock';
import { programApiErrorMessage, programLinkingExplanation } from './fields';
import ProgramLinkingLineItemsTable from './ProgramLinkingLineItemsTable';
import {
	useCreateProgramMutation,
	useGetKeyenceFilenameSuggestionsQuery,
	useGetLinkableLineItemsQuery,
	useUpdateProgramMutation,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import {
	normalizeCamProgrammerUserId,
	useCamProgrammerUserOptions,
} from './useCamProgrammerUserOptions';
import type { ProgramUser } from '../../interfaces/CamProgramming.interface';
import {
	programTypeUsesFilename,
	type KeyenceFilenameSuggestion,
	type LinkableLineItem,
	type ProgramBasedOnSummary,
	type ProgramType,
} from '../../interfaces/CamProgramming.interface';
import isInternalOrdersManager from '../../utils/isInternalOrdersManager';
import { PROGRAM_FILENAME_PLACEHOLDER } from './authors';
import {
	PROGRAMMING_DURATION_HINT,
	programFilenameHint,
} from './fieldHints';
import {
	PROGRAMMING_LOG_STATUS_LABELS,
	type ProgrammingLogStatus,
	programmingLogStatus,
} from './programmingStatus';
import { resolveCompletedSessionStartedAt } from './sessionLogging';
import type { ProgramImageHandlers } from './useProgramImages';
import {
	queuedImageIds,
	useQueuedProgramImages,
	withoutQueuedImages,
} from './queuedProgramImages';

const { Text } = Typography;

export interface ProgrammingStatsValues {
	filename?: string;
	based_on_program?: string | null;
	programming_status: ProgrammingLogStatus;
	programming_duration_minutes: number | null;
	programmer?: string | null;
	programmer_feedback?: string;
	/** Sibling line items the user ticked to apply this program to as well. */
	linked_line_items?: string[];
}

export interface ProgrammingStatsPayloadOptions {
	isEdit?: boolean;
	existingStartedAt?: string | null;
	existingCompletedAt?: string | null;
	/** When omitted, timestamps default to the current time on create. */
	now?: () => string;
}

const DUPLICATE_FILENAME_MESSAGE =
	'A program with this filename already exists for this part and program type.';

const normalizeProgramFilename = (value: string | null | undefined) =>
	String(value ?? '').trim();

/** Prefer an explicit filename, then the part name — empty strings count as missing. */
export const defaultProgramFilename = (
	filename: string | null | undefined,
	partName: string | null | undefined,
	programType: ProgramType,
	isEdit: boolean
) => {
	const normalizedFilename = normalizeProgramFilename(filename);
	if (normalizedFilename) {
		return normalizedFilename;
	}
	if (programType === 'cam' || (programTypeUsesFilename(programType) && !isEdit)) {
		return normalizeProgramFilename(partName);
	}
	return '';
};

const buildFilenameRules = (
	programType: ProgramType,
	takenFilenames: string[]
) => {
	if (!programTypeUsesFilename(programType)) {
		return [];
	}

	return [
		{ required: true, message: 'Enter the program filename' },
		{
			validator: async (_: unknown, value: string | null | undefined) => {
				const normalized = normalizeProgramFilename(value);
				if (!normalized) {
					return;
				}
				const duplicate = takenFilenames.some(
					(filename) => normalizeProgramFilename(filename) === normalized
				);
				if (duplicate) {
					throw new Error(DUPLICATE_FILENAME_MESSAGE);
				}
			},
		},
	];
};

const useShopManagerProgrammerPicker = (
	pinnedProgrammer?: ProgramUser | null
) => {
	const { data: currentUser } = useGetLoggedInUserQuery();
	const allowProgrammerOverride = isInternalOrdersManager(currentUser);
	const { userOptions: programmerOptions, isLoading: isLoadingUsers } =
		useCamProgrammerUserOptions({
			skip: !allowProgrammerOverride,
			pinnedUsers: [pinnedProgrammer],
		});

	return {
		currentUser,
		allowProgrammerOverride,
		programmerOptions,
		isLoadingUsers,
	};
};

/** Fields shared by the log and edit programming-stats dialogs. */
export const ProgrammingStatsFormFields: React.FC<{
	allowProgrammerOverride: boolean;
	programmerOptions: { label: string; value: string }[];
	loadingProgrammerOptions?: boolean;
	programType: ProgramType;
	isEdit?: boolean;
	basedOnOptions?: Pick<ProgramBasedOnSummary, 'id' | 'filename'>[];
	showBasedOnProgram?: boolean;
	keyenceFilenameSuggestions?: KeyenceFilenameSuggestion[];
	loadingKeyenceFilenameSuggestions?: boolean;
	/** Image handlers for the notes editor; see `useQueuedProgramImages`. */
	images: ProgramImageHandlers;
	takenFilenames?: string[];
	linkableLineItems?: LinkableLineItem[];
}> = ({
	allowProgrammerOverride,
	programmerOptions,
	loadingProgrammerOptions,
	programType,
	isEdit = false,
	basedOnOptions = [],
	showBasedOnProgram = false,
	keyenceFilenameSuggestions = [],
	loadingKeyenceFilenameSuggestions = false,
	images,
	takenFilenames = [],
	linkableLineItems = [],
}) => {
	const programmingStatus = Form.useWatch('programming_status');
	const isInProgress = programmingStatus === 'in_progress';
	const keyenceSuggestionOptions = keyenceFilenameSuggestions
		.filter((suggestion) => !suggestion.already_logged)
		.map((suggestion) => ({
			value: suggestion.filename,
		}));

	return (
		<>
			{programTypeUsesFilename(programType) && (
				<Form.Item
					name="filename"
					label="Program Filename"
					tooltip={programFilenameHint(programType, isEdit)}
					rules={buildFilenameRules(programType, takenFilenames)}
				>
					{programType === 'keyence' && !isEdit ? (
						<AutoComplete
							options={keyenceSuggestionOptions}
							placeholder={
								loadingKeyenceFilenameSuggestions
									? 'Loading inspection plan suggestions…'
									: keyenceSuggestionOptions.length > 0
										? 'Select or type a program filename'
										: PROGRAM_FILENAME_PLACEHOLDER
							}
							filterOption={(input, option) =>
								(option?.value ?? '')
									.toString()
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							notFoundContent={
								loadingKeyenceFilenameSuggestions ? (
									<Spin size="small" />
								) : null
							}
						/>
					) : (
						<Input placeholder={PROGRAM_FILENAME_PLACEHOLDER} />
					)}
				</Form.Item>
			)}
			{showBasedOnProgram && basedOnOptions.length > 0 && (
				<Form.Item
					name="based_on_program"
					label="Based On Program"
					tooltip="Optional — track which existing CAM program this one was derived from."
				>
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						placeholder="Select a program (optional)"
						options={basedOnOptions.map((program) => ({
							value: program.id,
							label:
								program.filename?.trim() || 'Unnamed program',
						}))}
					/>
				</Form.Item>
			)}
			{allowProgrammerOverride && (
				<Form.Item name="programmer" label="Programmer">
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						loading={loadingProgrammerOptions}
						options={programmerOptions}
					/>
				</Form.Item>
			)}
			<Form.Item
				name="programming_status"
				label="Programming status"
				rules={[{ required: true, message: 'Choose a programming status' }]}
			>
				<Radio.Group
					optionType="button"
					buttonStyle="solid"
					options={(
						Object.entries(PROGRAMMING_LOG_STATUS_LABELS) as [
							ProgrammingLogStatus,
							string,
						][]
					).map(([value, label]) => ({ value, label }))}
				/>
			</Form.Item>
			{isInProgress ? null : (
				<Form.Item
					name="programming_duration_minutes"
					label="Programming Duration (min)"
					tooltip={PROGRAMMING_DURATION_HINT}
					rules={[{ required: true, message: 'Enter the programming time' }]}
				>
					<InputNumber
						min={0}
						style={{ width: '100%' }}
						placeholder="Minutes"
					/>
				</Form.Item>
			)}
			<Form.Item
				name="programmer_feedback"
				label="Notes"
				tooltip="Comments, setup notes, or anything the next person should know about this program."
			>
				<RichTextFormField
					{...images}
					placeholder="Program notes... Paste or drop images directly."
					minHeight={240}
				/>
			</Form.Item>
			{linkableLineItems.length > 0 && (
				<>
					<Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
						{programLinkingExplanation(programType)}
					</Text>
					<Form.Item name="linked_line_items" label="Other batches on this order">
						{/* Unticked by default: applying one person's program to
						    another line item is a claim about the work, so it is
						    the programmer's call rather than a convenience. */}
						<ProgramLinkingLineItemsTable lineItems={linkableLineItems} />
					</Form.Item>
				</>
			)}
		</>
	);
};

export const buildProgrammingStatsPayload = (
	values: ProgrammingStatsValues,
	allowProgrammerOverride: boolean,
	programType: ProgramType,
	options: ProgrammingStatsPayloadOptions = {}
) => {
	const now = options.now ?? (() => new Date().toISOString());
	const wasCompleted =
		!!options.existingCompletedAt &&
		programmingLogStatus(
			options.existingStartedAt,
			options.existingCompletedAt
		) === 'completed';

	const shared = {
		...(programTypeUsesFilename(programType)
			? { filename: normalizeProgramFilename(values.filename) }
			: {}),
		...(programType === 'cam' && values.based_on_program
			? { based_on_program: values.based_on_program }
			: {}),
		programmer_feedback: values.programmer_feedback?.trim() || null,
		...(allowProgrammerOverride && values.programmer
			? { programmer: values.programmer }
			: {}),
		...(values.linked_line_items?.length
			? { order_line_items: values.linked_line_items }
			: {}),
	};

	if (values.programming_status === 'in_progress') {
		const startedAt =
			options.isEdit && options.existingStartedAt
				? options.existingStartedAt
				: now();
		return {
			...shared,
			programming_started_at: startedAt,
			programming_completed_at: null,
			programming_duration_minutes: 0,
		};
	}

	const completedAt =
		options.isEdit && wasCompleted && options.existingCompletedAt
			? options.existingCompletedAt
			: now();
	const duration = values.programming_duration_minutes ?? 0;
	const startedAt = resolveCompletedSessionStartedAt(
		options.existingStartedAt,
		completedAt,
		duration
	);

	return {
		...shared,
		programming_duration_minutes: duration,
		programming_completed_at: completedAt,
		...(startedAt ? { programming_started_at: startedAt } : {}),
	};
};

export interface ProgrammingStatsFormModalProps {
	open: boolean;
	onClose: () => void;
	programType: ProgramType;
	orderLineItemId: string;
	/** Prefills the filename on create when no initial filename is set. */
	orderLineItemName?: string | null;
	/** When set, updates the existing program instead of creating one. */
	programId?: string | null;
	initialProgrammerId?: string | null;
	pinnedProgrammer?: ProgramUser | null;
	initialFilename?: string | null;
	initialDurationMinutes?: number | null;
	initialStartedAt?: string | null;
	initialCompletedAt?: string | null;
	initialProgrammerFeedback?: string | null;
	/** Existing CAM programs on the same part — enables the based-on picker. */
	existingPrograms?: Pick<ProgramBasedOnSummary, 'id' | 'filename'>[];
	onSuccess?: () => void;
}

const ProgrammingStatsFormModal: React.FC<ProgrammingStatsFormModalProps> = ({
	open,
	onClose,
	programType,
	orderLineItemId,
	orderLineItemName = null,
	programId,
	initialProgrammerId = null,
	pinnedProgrammer = null,
	initialFilename = null,
	initialDurationMinutes = null,
	initialStartedAt = null,
	initialCompletedAt = null,
	initialProgrammerFeedback = null,
	existingPrograms = [],
	onSuccess,
}) => {
	const [form] = Form.useForm<ProgrammingStatsValues>();
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [questionBlock, setQuestionBlock] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	/**
	 * Set once the log has created the program, so a failure while attaching
	 * its images leaves a retry updating that program rather than logging a
	 * second one.
	 */
	const [createdProgramId, setCreatedProgramId] = useState<string | null>(null);
	const [createProgram] = useCreateProgramMutation();
	const [updateProgram] = useUpdateProgramMutation();
	const {
		imageProps,
		attachQueuedImages,
		reset: forgetQueuedImages,
	} = useQueuedProgramImages(programId ?? createdProgramId);
	const { allowProgrammerOverride, programmerOptions, currentUser, isLoadingUsers } =
		useShopManagerProgrammerPicker(pinnedProgrammer);
	const { data: keyenceSuggestionData, isLoading: loadingKeyenceSuggestions } =
		useGetKeyenceFilenameSuggestionsQuery(
			{
				orderLineItemId,
				excludeProgram: programId,
			},
			{
				skip:
					!open ||
					programType !== 'keyence' ||
					!!programId ||
					!orderLineItemId,
			}
		);
	const { data: linkableData } = useGetLinkableLineItemsQuery(
		{ orderLineItemId, program: programId, programType },
		{ skip: !open || !orderLineItemId }
	);

	const isEdit = !!programId;
	const showBasedOnProgram =
		programType === 'cam' && !isEdit && existingPrograms.length > 0;
	const programmerOptionsReady =
		!allowProgrammerOverride || (!isLoadingUsers && programmerOptions.length > 0);
	const takenFilenames = existingPrograms.map((program) => program.filename);

	useEffect(() => {
		if (!open) {
			setSubmitError(null);
			setQuestionBlock(null);
			return;
		}

		if (!programmerOptionsReady) {
			return;
		}

		const defaultProgrammerId = allowProgrammerOverride
			? normalizeCamProgrammerUserId(initialProgrammerId) ??
				(programmerOptions.some(
					(option) =>
						option.value === normalizeCamProgrammerUserId(currentUser?.id)
				)
					? normalizeCamProgrammerUserId(currentUser?.id)
					: null)
			: null;

		const defaultFilename = defaultProgramFilename(
			initialFilename,
			orderLineItemName,
			programType,
			isEdit
		);

		const logStatus = programmingLogStatus(initialStartedAt, initialCompletedAt);

		form.setFieldsValue({
			filename: defaultFilename,
			based_on_program: null,
			programmer: defaultProgrammerId,
			programming_status: logStatus,
			programming_duration_minutes: initialDurationMinutes,
			programmer_feedback: initialProgrammerFeedback ?? '',
			linked_line_items: [],
		});
	}, [
		form,
		open,
		isEdit,
		allowProgrammerOverride,
		programmerOptionsReady,
		currentUser?.id,
		initialProgrammerId,
		initialFilename,
		orderLineItemName,
		programType,
		initialDurationMinutes,
		initialStartedAt,
		initialCompletedAt,
		initialProgrammerFeedback,
	]);

	useEffect(() => {
		if (!open || !isEdit || !linkableData?.results) {
			return;
		}

		form.setFieldsValue({
			linked_line_items: linkableData.results
				.filter((lineItem) => lineItem.already_linked)
				.map((lineItem) => lineItem.id),
		});
	}, [form, open, isEdit, linkableData?.results]);

	const handleClose = () => {
		setSubmitError(null);
		setQuestionBlock(null);
		setCreatedProgramId(null);
		forgetQueuedImages();
		form.resetFields();
		onClose();
	};

	const handleSubmit = async (submittedValues: ProgrammingStatsValues) => {
		setSubmitError(null);
		setQuestionBlock(null);
		const values = {
			...form.getFieldsValue(true),
			...submittedValues,
		} as ProgrammingStatsValues;
		const linkableResults = linkableData?.results ?? [];
		const target = programId ?? createdProgramId;
		const payload = buildProgrammingStatsPayload(
			values,
			allowProgrammerOverride,
			programType,
			{
				isEdit: !!target,
				existingStartedAt: initialStartedAt,
				existingCompletedAt: initialCompletedAt,
			}
		);
		const notes = payload.programmer_feedback ?? '';
		const notesCarryQueuedImages = queuedImageIds(notes).length > 0;

		if (target && linkableResults.length > 0) {
			const linkedSiblingIds = values.linked_line_items ?? [];
			payload.order_line_items = [
				...new Set([orderLineItemId, ...linkedSiblingIds]),
			];
		}

		setIsSaving(true);
		try {
			if (target) {
				await updateProgram({
					id: target,
					values: {
						...payload,
						...(notesCarryQueuedImages
							? {
									programmer_feedback: await attachQueuedImages(
										target,
										notes
									),
								}
							: {}),
					},
				}).unwrap();
				message.success(
					programId
						? 'Programming stats updated'
						: 'Programming stats logged'
				);
			} else {
				const created = await createProgram({
					program_type: programType,
					order_line_item: orderLineItemId,
					...payload,
					// Images are stored against a program, so this first write
					// carries the prose alone and the one below adds them once
					// there is an id to attach them to.
					...(notesCarryQueuedImages
						? { programmer_feedback: withoutQueuedImages(notes) || null }
						: {}),
				}).unwrap();

				if (notesCarryQueuedImages) {
					setCreatedProgramId(created.id);
					await updateProgram({
						id: created.id,
						values: {
							programmer_feedback: await attachQueuedImages(
								created.id,
								notes
							),
						},
					}).unwrap();
				}
				message.success('Programming stats logged');
			}
			handleClose();
			onSuccess?.();
		} catch (error) {
			const block = getProductionQuestionBlockMessage(error);
			if (block) {
				setQuestionBlock(block);
				return;
			}
			setSubmitError(
				programApiErrorMessage(
					error,
					programId
						? 'Failed to update programming stats'
						: 'Failed to log programming stats'
				)
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			className="cam-modal"
			title={isEdit ? 'Edit Programming Stats' : 'Log Programming Stats'}
			open={open}
			centered
			width={900}
			onCancel={handleClose}
			onOk={() => form.submit()}
			okText={isEdit ? 'Save' : 'Log'}
			confirmLoading={isSaving}
			destroyOnClose
		>
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				{submitError && (
					<Alert
						type="error"
						showIcon
						message={submitError}
						style={{ marginBottom: 16 }}
					/>
				)}
				{questionBlock && (
					<Alert
						type="warning"
						showIcon
						message={questionBlock}
						description={PRODUCTION_QUESTION_BLOCK_HINT}
						style={{ marginBottom: 16 }}
					/>
				)}
				<ProgrammingStatsFormFields
					allowProgrammerOverride={allowProgrammerOverride}
					programmerOptions={programmerOptions}
					loadingProgrammerOptions={isLoadingUsers}
					programType={programType}
					isEdit={isEdit}
					basedOnOptions={existingPrograms}
					showBasedOnProgram={showBasedOnProgram}
					keyenceFilenameSuggestions={
						keyenceSuggestionData?.suggestions ?? []
					}
					loadingKeyenceFilenameSuggestions={loadingKeyenceSuggestions}
					images={imageProps}
					takenFilenames={takenFilenames}
					linkableLineItems={linkableData?.results ?? []}
				/>
				{!isEdit && !allowProgrammerOverride && (
					<Text type="secondary">
						You will be recorded as the programmer for this part.
					</Text>
				)}
			</Form>
		</Modal>
	);
};

export default ProgrammingStatsFormModal;
